import { useState, useEffect } from 'react';

/**
 * Hook for loading and persisting chat messages.
 * @param {string} chatId
 * @returns {{ messages: Array, loading: boolean, setMessages: Function, persistMessage: Function }}
 */
export default function useChatMessages(chatId) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }

    let isMounted = true;
    setLoading(true);

    (async function load() {
      try {
        // Fetch existing messages
        const res = await fetch(`/api/chats/${chatId}/messages`);
        const rows = await res.json();

        // Map to internal format
        let mapped = rows.map(r => ({
          key: `${r.sender}-${r.timestamp}`,
          sender: r.sender,
          text:   r.content,
          timestamp: r.timestamp,
        }));

        // Inject remix note if applicable
        try {
          const metaRes = await fetch(`/api/chats/${chatId}`);
          if (metaRes.ok) {
            const { parent_chat_id, parent_version, parent_username } = await metaRes.json();
            if (parent_chat_id) {
              const origin = window.location.origin;
              const link = parent_version != null
                ? `${origin}/${parent_username}/${parent_chat_id}/${parent_version}`
                : `${origin}/${parent_username}/${parent_chat_id}`;

              mapped.unshift({
                key: `remix-${Date.now()}`,
                sender: 'ai',
                text: (
                  <span>
                    Remix from:{' '}
                    <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
                  </span>
                ),
                timestamp: new Date().toISOString(),
              });
            }
          }
        } catch (err) {
          console.error('Failed to load remix metadata:', err);
        }

        if (isMounted) {
          setMessages(mapped);
        }
      } catch (err) {
        console.error('Failed to load messages:', err);
        if (isMounted) setMessages([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [chatId]);

  /**
   * Persist a single message to the backend.
   * @param {'user'|'ai'} sender
   * @param {string} content
   * @returns {Promise<string|null>} messageId on success, or null
   */
  async function persistMessage(sender, content) {
    if (!chatId) return null;
    try {
      const res = await fetch(`/api/chats/${chatId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender, content }),
      });
      if (!res.ok) throw new Error('Persist failed');
      const { messageId } = await res.json();
      return messageId;
    } catch (err) {
      console.error('Failed to persist message:', err);
      return null;
    }
  }

  return { messages, setMessages, loading, persistMessage };
}
